import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "@/components/ui-bits";
import { roleLabel, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Key } from "@phosphor-icons/react";

function ResetPasswordButton({ u, onDone }) {
  const [temp, setTemp] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/users/${u.user_id}/reset-password`);
      setTemp(r.data.temp_password);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset password");
    } finally { setBusy(false); }
  };
  return (
    <Dialog onOpenChange={(o) => { if (!o) setTemp(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-8 text-xs" data-testid={`reset-pw-${u.email}`}>
          <Key size={14} weight="bold" /> Reset
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm sm:max-w-md">
        <DialogHeader><DialogTitle>Reset password — {u.name}</DialogTitle></DialogHeader>
        {!temp ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">This generates a one-time temporary password for <strong>{u.email}</strong>. Share it with them securely — they'll be required to set a new password on next login.</p>
            <Button onClick={reset} disabled={busy} className="rounded-sm w-full" data-testid="confirm-reset-pw">{busy ? "Generating…" : "Generate temporary password"}</Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="temp-password-result">
            <p className="text-sm text-muted-foreground">Share this temporary password with <strong>{u.email}</strong>. They must change it on next login:</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={temp} className="rounded-sm mono text-sm" onFocus={(e) => e.target.select()} data-testid="temp-password-value" />
              <Button className="rounded-sm" onClick={() => { navigator.clipboard?.writeText(temp); toast.success("Copied"); }}>Copy</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    if (user.role === "super_admin") api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (u, role) => {
    await api.patch(`/users/${u.user_id}`, { role });
    toast.success("Role updated");
    load();
  };

  const ROLES = user.role === "super_admin"
    ? ["super_admin", "client_admin", "manager", "agent", "auditor"]
    : ["client_admin", "manager", "agent", "auditor"];

  return (
    <div data-testid="users-page">
      <PageHeader title="User Management" subtitle="Invite users and assign roles">
        <InviteDialog onDone={load} tenants={tenants} isSuper={user.role === "super_admin"} />
      </PageHeader>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps border-b border-border bg-secondary/40">
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-center p-3">Status</th>
              <th className="text-left p-3">Joined</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b border-border last:border-0 hover:bg-secondary/50" data-testid={`user-row-${u.email}`}>
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3 mono text-xs">{u.email}</td>
                <td className="p-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.user_id === user.user_id}>
                    <SelectTrigger className="w-36 h-8 rounded-sm text-xs" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-center"><StatusBadge status={u.status} /></td>
                <td className="p-3 text-muted-foreground text-xs">{fmtDate(u.created_at)}</td>
                <td className="p-3 text-right">
                  {u.user_id !== user.user_id && <ResetPasswordButton u={u} onDone={load} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <Empty text="No users yet" />}
      </div>
    </div>
  );
}

function InviteDialog({ onDone, tenants, isSuper }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "agent", tenant_id: "" });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    if (!f.email || !f.name) return toast.error("Name and email required");
    if (isSuper && !f.tenant_id && f.role !== "super_admin") return toast.error("Select a tenant");
    try {
      await api.post("/users", { ...f, tenant_id: f.tenant_id || null });
      toast.success("User invited");
      setOpen(false);
      setF({ name: "", email: "", role: "agent", tenant_id: "" });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const ROLES = isSuper
    ? ["super_admin", "client_admin", "manager", "agent", "auditor"]
    : ["client_admin", "manager", "agent", "auditor"];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm gap-2" data-testid="invite-user-button"><UserPlus size={16} weight="bold" /> Invite User</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle>Invite User</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Full Name</Label><Input className="rounded-sm" value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="invite-name" /></div>
          <div><Label className="text-xs">Work Email (Google)</Label><Input className="rounded-sm" value={f.email} onChange={(e) => upd("email", e.target.value)} data-testid="invite-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Role</Label>
              <Select value={f.role} onValueChange={(v) => upd("role", v)}>
                <SelectTrigger className="rounded-sm" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>)}</SelectContent>
              </Select></div>
            {isSuper && f.role !== "super_admin" && (
              <div><Label className="text-xs">Tenant</Label>
                <Select value={f.tenant_id} onValueChange={(v) => upd("tenant_id", v)}>
                  <SelectTrigger className="rounded-sm" data-testid="invite-tenant"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.company_name}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">The user gains access when they sign in with this Google email.</p>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm" data-testid="save-invite-button">Send Invite</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
